/**
 * Browser Orderbook Tracker
 * Replicates orderbook-live.js functionality for browser environment
 * 
 * Key differences from Electron:
 * - No child process (runs in main thread)
 * - Uses Hyperliquid SDK directly (same as Electron worker)
 * - Emits CustomEvent instead of HTTP POST
 * - Singleton pattern to ensure only one instance per asset
 */

import { WebSocketTransport, SubscriptionClient } from '@nktkas/hyperliquid';

export class BrowserOrderbookTracker {
    constructor() {
        this.transport = null;
        this.subsClient = null;
        this.subscription = null;
        this.currentAsset = null;
        this.isConnected = false;
        this.isRunning = false;
        this.lastUpdate = null;
        this.orderbook = {
            bids: [],
            asks: [],
            timestamp: null
        };
    }

    /**
     * Start orderbook tracking for specified asset
     * @param {string} asset - Asset symbol (e.g., 'BTC', 'ETH')
     */
    async start(asset) {
        if (this.isRunning && this.currentAsset === asset) {
            // console.log(`[ORDERBOOK-TRACKER] Already running for ${asset}`);
            return;
        }

        // Stop existing subscription if switching assets
        if (this.isRunning && this.currentAsset !== asset) {
            await this.stop();
        }

        try {
            // console.log(`[ORDERBOOK-TRACKER] Starting for ${asset}`);
            // Don't uppercase HIP-3 assets (xyz:NVDA must stay lowercase prefix)
            this.currentAsset = asset.includes(':') ? asset : asset.toUpperCase();
            const subscribedAsset = this.currentAsset;  // Capture asset for closure to prevent race conditions

            // Create WebSocket transport (same config as orderbook-live.js)
            this.transport = new WebSocketTransport({
                url: 'wss://api.hyperliquid.xyz/ws',
                timeout: 10000,
                keepAliveInterval: 30000,
                resubscribe: true,
                reconnect: {
                    maxRetries: Infinity,
                    connectionTimeout: 10000,
                    reconnectionDelay: (attempt) => Math.min(1000 * Math.pow(1.5, attempt), 30000)
                }
            });

            // Create subscription client
            this.subsClient = new SubscriptionClient({ transport: this.transport });

            // Wait for connection
            await this.transport.ready();
            this.isConnected = true;
            // console.log(`[ORDERBOOK-TRACKER] Connected to WebSocket`);

            // Subscribe to orderbook (matches orderbook-live.js line 75)
            await this.subscribeToOrderbook(subscribedAsset);

            this.isRunning = true;

        } catch (error) {
            // console.error(`[ORDERBOOK-TRACKER] Failed to start:`, error);
            throw error;
        }
    }

    /**
     * Subscribe to asset L2 orderbook updates
     * Matches orderbook-live.js subscribeToOrderbook() method
     * @param {string} subscribedAsset - Asset to subscribe to (captured to prevent race conditions)
     */
    async subscribeToOrderbook(subscribedAsset) {
        try {
            this.subscription = await this.subsClient.l2Book(
                {
                    coin: subscribedAsset
                    // NOTE: WebSocket l2Book subscription only accepts 'coin' parameter
                    // Other parameters (nSigFigs, mantissa, nLevels) are only for HTTP Info endpoint
                    // API will return whatever depth it provides by default (~10-20 levels)
                },
                (data) => this.handleOrderbookUpdate(data, subscribedAsset)
            );

            // console.log(`[ORDERBOOK-TRACKER] Subscribed to ${subscribedAsset} orderbook`);

        } catch (error) {
            // console.error('[ORDERBOOK-TRACKER] Failed to subscribe:', error);
            throw error;
        }
    }
    /**
     * Handle incoming orderbook updates
     * Matches orderbook-live.js handleOrderbookUpdate() method
     * @param {object} data - Orderbook data from WebSocket
     * @param {string} subscribedAsset - Asset this subscription is for (prevents race conditions)
     */
    handleOrderbookUpdate(data, subscribedAsset) {
        try {
            // Ignore updates if not running or no asset selected (prevent stale data during asset switch)
            if (!this.isRunning || !this.currentAsset) {
                // console.log('[ORDERBOOK-TRACKER] IGNORING update - not running or no asset');
                return;
            }

            // Parse Hyperliquid L2Book format: levels[0] = bids, levels[1] = asks
            let bids = [];
            let asks = [];

            if (data.levels && Array.isArray(data.levels) && data.levels.length >= 2) {
                // Convert Hyperliquid format to [price, size] arrays
                bids = data.levels[0].map(level => [level.px, level.sz]);
                asks = data.levels[1].map(level => [level.px, level.sz]);
            }

            this.orderbook = {
                bids: bids,
                asks: asks,
                timestamp: Date.now()
            };

            this.lastUpdate = new Date().toISOString();

            const bestBid = this.orderbook.bids[0]?.[0];
            const bestAsk = this.orderbook.asks[0]?.[0];
            const spread = bestAsk && bestBid ? (parseFloat(bestAsk) - parseFloat(bestBid)).toFixed(8) : null;

            // console.log(`[ORDERBOOK-TRACKER] Broadcasting ${subscribedAsset} data - bid: ${bestBid}, ask: ${bestAsk}`);

            // Broadcast to UI (send all levels, let terminal-view slice as needed)
            const payload = {
                type: 'orderbook-data',
                asset: subscribedAsset,
                data: {
                    bids: this.orderbook.bids,  // Send all 50 levels
                    asks: this.orderbook.asks,  // Send all 50 levels
                    timestamp: this.orderbook.timestamp,
                    bestBid: bestBid,
                    bestAsk: bestAsk,
                    spread: spread
                },
                lastUpdate: this.lastUpdate,
                connected: this.isConnected
            };

            window.dispatchEvent(new CustomEvent('orderbook:external-data', {
                detail: payload
            }));

        } catch (error) {
            // console.error('[ORDERBOOK-TRACKER] Error broadcasting:', error);
        }
    }

    /**
     * Stop orderbook tracking
     */
    async stop() {
        try {
            // console.log(`[ORDERBOOK-TRACKER] Stopping for ${this.currentAsset}`);

            // Set flags first to prevent any pending callbacks from broadcasting
            this.isRunning = false;
            this.isConnected = false;
            const oldAsset = this.currentAsset;
            this.currentAsset = null;

            if (this.subscription) {
                // console.log(`[ORDERBOOK-TRACKER] Unsubscribing from ${oldAsset}`);
                await this.subscription.unsubscribe();
                this.subscription = null;
            }

            if (this.subsClient) {
                this.subsClient = null;
            }

            // CRITICAL: Close the WebSocket transport to stop ALL data flow
            if (this.transport) {
                // console.log(`[ORDERBOOK-TRACKER] Closing WebSocket transport for ${oldAsset}`);
                await this.transport.close();
                this.transport = null;
            }

            // console.log(`[ORDERBOOK-TRACKER] Stopped ${oldAsset}`);

        } catch (error) {
            // console.error('[ORDERBOOK-TRACKER] Error stopping:', error);
        }
    }

    /**
     * Change asset (stop current, start new)
     */
    async changeAsset(newAsset) {
        await this.stop();
        await this.start(newAsset);
    }
}

// Create singleton instance
if (typeof window !== 'undefined') {
    window.BrowserOrderbookTracker = new BrowserOrderbookTracker();
    // console.log('✅ BrowserOrderbookTracker initialized');
}

export default BrowserOrderbookTracker;
